import React, { useState } from 'react';
import { LayoutRequest, LayoutResponse } from '../types';
import { layoutApi } from '../api/client';

interface Props {
  response: LayoutResponse;
  request?: LayoutRequest | null;
}

// ── PR loss waterfall ─────────────────────────────────────────────────────────

const PR_LABELS: Record<string, string> = {
  inverter:     'Inverter',
  cable:        'Cable',
  soiling:      'Soiling',
  mismatch:     'Mismatch',
  temperature:  'Temperature',
  shading:      'Shading',
  availability: 'Availability',
  transformer:  'Transformer',
};

const PR_COLORS: Record<string, string> = {
  inverter:     '#3b82f6',
  cable:        '#f59e0b',
  soiling:      '#f97316',
  mismatch:     '#a855f7',
  temperature:  '#ef4444',
  shading:      '#06b6d4',
  availability: '#84cc16',
  transformer:  '#64748b',
};

function PRWaterfall({ pr }: { pr: LayoutResponse['yield_']['pr_breakdown'] }) {
  const keys = Object.keys(PR_LABELS) as (keyof typeof PR_LABELS)[];
  const losses = keys.map(k => ({ key: k, loss: 1 - (pr as any)[k] }));
  const totalLoss = losses.reduce((sum, l) => sum + l.loss, 0);
  const composite = pr.composite;

  return (
    <div>
      {/* Stacked loss bar */}
      <div style={{ display: 'flex', height: 18, borderRadius: 4, overflow: 'hidden', marginBottom: 6 }}>
        {/* Composite PR (green) */}
        <div style={{
          width: `${composite * 100}%`,
          background: 'linear-gradient(90deg, #16a34a, #22c55e)',
          minWidth: 2,
        }} title={`Composite PR: ${(composite * 100).toFixed(1)}%`} />
        {/* Each loss segment */}
        {losses.map(({ key, loss }) => (
          <div key={key} style={{
            width: `${loss * 100}%`,
            background: PR_COLORS[key],
            minWidth: loss > 0.001 ? 2 : 0,
            opacity: 0.85,
          }} title={`${PR_LABELS[key]}: −${(loss * 100).toFixed(1)}%`} />
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 10px', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: '#22c55e', flexShrink: 0 }} />
          <span style={legendText}>PR {(composite * 100).toFixed(1)}%</span>
        </div>
        {losses.map(({ key, loss }) => loss > 0 && (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: PR_COLORS[key], flexShrink: 0 }} />
            <span style={legendText}>{PR_LABELS[key]} −{(loss * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Sensitivity tornado ───────────────────────────────────────────────────────

interface TornadoRow {
  label: string;
  lowDelta: number;
  highDelta: number;
  lowPct: number;
  highPct: number;
}

function TornadoChart({ rows, base }: { rows: TornadoRow[]; base: number }) {
  const maxAbs = Math.max(...rows.flatMap(r => [Math.abs(r.lowDelta), Math.abs(r.highDelta)]));
  if (maxAbs === 0) return null;

  const BAR_W = 190; // total available px for ±bars
  const HALF  = BAR_W / 2;

  return (
    <div style={{ marginTop: 8 }}>
      {/* Center label */}
      <div style={{ textAlign: 'center', fontSize: 10, color: '#888', marginBottom: 6 }}>
        Base: {Math.round(base).toLocaleString()} MWh/yr
      </div>
      <svg width="100%" viewBox={`0 0 240 ${rows.length * 28 + 10}`}
        style={{ display: 'block', overflow: 'visible' }}>

        {/* Center line */}
        <line x1={120} y1={0} x2={120} y2={rows.length * 28 + 6}
          stroke="#d1d5db" strokeWidth={1} />

        {rows.map((row, i) => {
          const y = i * 28 + 4;
          // Low (left) bar
          const lowW  = (Math.abs(row.lowDelta)  / maxAbs) * HALF * 0.92;
          // High (right) bar
          const highW = (Math.abs(row.highDelta) / maxAbs) * HALF * 0.92;

          return (
            <g key={row.label}>
              {/* Low bar — left of centre */}
              <rect x={120 - lowW} y={y} width={lowW} height={18}
                fill="#f97316" rx={2} opacity={0.85}>
                <title>{`${row.label} low: ${row.lowPct.toFixed(1)}%`}</title>
              </rect>
              {/* High bar — right of centre */}
              <rect x={120} y={y} width={highW} height={18}
                fill="#22c55e" rx={2} opacity={0.85}>
                <title>{`${row.label} high: +${row.highPct.toFixed(1)}%`}</title>
              </rect>
              {/* Label */}
              <text x={0} y={y + 13} fontSize={11} fill="#555" fontFamily="Inter,sans-serif">
                {row.label}
              </text>
              {/* Low value */}
              {lowW > 16 && (
                <text x={120 - lowW / 2} y={y + 12} fontSize={9}
                  fill="#fff" textAnchor="middle" fontFamily="Inter,sans-serif">
                  {row.lowPct.toFixed(1)}%
                </text>
              )}
              {/* High value */}
              {highW > 16 && (
                <text x={120 + highW / 2} y={y + 12} fontSize={9}
                  fill="#fff" textAnchor="middle" fontFamily="Inter,sans-serif">
                  +{row.highPct.toFixed(1)}%
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#888', marginTop: 2 }}>
        <span style={{ color: '#f97316' }}>◀ downside</span>
        <span style={{ color: '#22c55e' }}>upside ▶</span>
      </div>
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function Row({ label, value, unit, highlight }: {
  label: string; value: string | number; unit?: string; highlight?: boolean;
}) {
  const display = typeof value === 'number' ? value.toLocaleString() : value;
  return (
    <div style={{ ...rowStyle, ...(highlight ? { background: '#f0f9f7' } : {}) }}>
      <span style={labelStyle}>{label}</span>
      <span style={{ ...valueStyle, ...(highlight ? { color: '#17607A' } : {}) }}>
        {display}
        {unit && <span style={unitStyle}> {unit}</span>}
      </span>
    </div>
  );
}

function Section({ title, color, children }: {
  title: string; color: string; children: React.ReactNode;
}) {
  return (
    <div style={{ ...sectionStyle, borderLeft: `3px solid ${color}` }}>
      <div style={{ ...titleStyle, color }}>{title}</div>
      {children}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ResultsPanel({ response, request }: Props) {
  const g = response.geometry;
  const y = response.yield_;
  const s = response.strings;
  const o = response.optimiser;

  const [tornadoLoading,  setTornadoLoading]  = useState(false);
  const [tornadoRows,     setTornadoRows]      = useState<TornadoRow[] | null>(null);
  const [tornadoError,    setTornadoError]     = useState('');
  const [tornadoOpen,     setTornadoOpen]      = useState(false);

  const pct = (v: number) => `${(v * 100).toFixed(1)}%`;

  const runTornado = async () => {
    if (!request) return;
    setTornadoLoading(true);
    setTornadoError('');
    setTornadoRows(null);

    const base     = y.annual_mwh;
    const baseGcr  = g.gcr_effective;
    const baseGhi  = request.yield_p?.ghi_kwh_m2_yr ?? y.ghi_kwh_m2_yr;
    const baseDcAc = request.inverter?.dc_ac_ratio   ?? 1.30;

    const variants: LayoutRequest[] = [
      // GHI −10%
      { ...request, yield_p:  { ghi_kwh_m2_yr: baseGhi * 0.90 } },
      // GHI +10%
      { ...request, yield_p:  { ghi_kwh_m2_yr: baseGhi * 1.10 } },
      // GCR −10%  (disable optimiser so we hold the value)
      { ...request, tracker:  { ...(request.tracker ?? {}), gcr: Math.max(baseGcr * 0.90, 0.20) }, optimise_gcr: false },
      // GCR +10%
      { ...request, tracker:  { ...(request.tracker ?? {}), gcr: Math.min(baseGcr * 1.10, 0.65) }, optimise_gcr: false },
      // DC/AC −10%
      { ...request, inverter: { ...(request.inverter ?? {}), dc_ac_ratio: Math.max(baseDcAc * 0.90, 1.0) } },
      // DC/AC +10%
      { ...request, inverter: { ...(request.inverter ?? {}), dc_ac_ratio: Math.min(baseDcAc * 1.10, 2.0) } },
    ];

    try {
      const settled = await Promise.allSettled(variants.map(v => layoutApi.generate(v)));
      const results = settled.map(r => r.status === 'fulfilled' ? r.value : null);
      if (results.every(r => r === null)) throw new Error('All 6 variants failed');
      const mwh = results.map(r => r?.yield_.annual_mwh ?? base);

      const makeRow = (label: string, lo: number, hi: number): TornadoRow => ({
        label,
        lowDelta:  lo - base,
        highDelta: hi - base,
        lowPct:   ((lo - base) / base) * 100,
        highPct:  ((hi - base) / base) * 100,
      });

      const rows = [
        makeRow('GHI ±10%',    mwh[0], mwh[1]),
        makeRow('GCR ±10%',    mwh[2], mwh[3]),
        makeRow('DC/AC ±10%',  mwh[4], mwh[5]),
      ].sort((a, b) =>
        (Math.abs(b.highDelta) + Math.abs(b.lowDelta)) -
        (Math.abs(a.highDelta) + Math.abs(a.lowDelta))
      );

      setTornadoRows(rows);
    } catch {
      setTornadoError('Sensitivity run failed. Check connection.');
    } finally {
      setTornadoLoading(false);
    }
  };

  return (
    <div style={panelStyle}>

      {/* ── KPI hero bar ─────────────────────────────────────────────────── */}
      <div style={kpiBar}>
        <KPI label="DC" value={g.total_dc_mwp.toFixed(1)} unit="MWp" color="#2E5FA3" />
        <KPI label="AC" value={g.total_ac_mw.toFixed(1)}  unit="MW"  color="#17607A" />
        <KPI label="Annual"
          value={(y.annual_mwh / 1000).toFixed(1)} unit="GWh/yr" color="#C55A11" />
        <KPI label="Sp. Yield"
          value={Math.round(y.specific_yield).toString()} unit="kWh/kWp" color="#4B2E83" />
      </div>

      {/* ── GCR optimiser callout ─────────────────────────────────────────── */}
      {o && (
        <div style={optimiserBox}>
          <div style={optimiserTitle}>GCR Optimised</div>
          <div style={optimiserRow}><span>Input GCR</span><span>{o.gcr_input.toFixed(3)}</span></div>
          <div style={optimiserRow}><span>Optimal GCR</span><strong>{o.gcr_optimal.toFixed(3)}</strong></div>
          <div style={optimiserRow}>
            <span>DC gain</span>
            <strong style={{ color: '#1E6B3C' }}>+{o.dc_gain_pct.toFixed(1)}%</strong>
          </div>
          <div style={optimiserRow}><span>Shadow limit</span><span>{o.gcr_max_shadow.toFixed(3)}</span></div>
        </div>
      )}

      <Section title="Power" color="#2E5FA3">
        <Row label="DC Capacity"   value={g.total_dc_mwp.toFixed(2)}    unit="MWp" />
        <Row label="Bifacial DC"   value={g.bifacial_dc_mwp.toFixed(2)} unit="MWp"
          highlight={g.bifacial_gain > 0} />
        <Row label="Bifacial Gain" value={pct(g.bifacial_gain)} />
        <Row label="AC Capacity"   value={g.total_ac_mw.toFixed(2)}     unit="MWac" />
        <Row label="DC:AC Ratio"   value={(g.total_dc_mwp / g.total_ac_mw).toFixed(2)} />
      </Section>

      <Section title="Layout" color="#17607A">
        <Row label="Total Tables"  value={g.total_tables.toLocaleString()} />
        <Row label="Total Panels"  value={g.total_panels.toLocaleString()} />
        <Row label="Rows × Blocks" value={`${g.n_rows} × ${g.n_blocks}`} />
        <Row label="GCR"           value={g.gcr_effective.toFixed(3)} />
        <Row label="Row Pitch"     value={g.row_pitch.toFixed(2)}  unit="m" />
        <Row label="Table Width"   value={g.table_w.toFixed(2)}    unit="m" />
        <Row label="PV Inverters"  value={g.n_pv_inverters} />
        {g.bess_coupling === 'AC' && (
          <Row label="BESS Inverters" value={g.n_bess_inverters} />
        )}
        <Row label="Coupling" value={`${g.bess_coupling}-Coupled`} />
      </Section>

      <Section title="Land Use" color="#4B2E83">
        <Row label="Site Area"      value={g.site_area_ha.toFixed(1)}     unit="ha" />
        <Row label="Buildable Area" value={g.net_area_ha.toFixed(1)}      unit="ha" />
        <Row label="Tracker Field"  value={g.tracker_field_ha.toFixed(1)} unit="ha" />
        <Row label="DC Density"     value={g.dc_mwp_per_ha.toFixed(3)}   unit="MWp/ha" />
        <Row label="AC Density"     value={g.ac_mw_per_ha.toFixed(3)}    unit="MWac/ha" />
      </Section>

      <Section title="Yield" color="#C55A11">
        <Row label="Annual Energy"   value={Math.round(y.annual_mwh).toLocaleString()} unit="MWh/yr" />
        <Row label="Specific Yield"  value={Math.round(y.specific_yield)} unit="kWh/kWp/yr" />
        <Row label="Capacity Factor" value={pct(y.capacity_factor)} />
        <Row label="PR Composite"    value={pct(y.pr_breakdown.composite)} />
        <Row label="Land (energy)"   value={y.land_mwh_per_ha.toFixed(0)} unit="MWh/ha/yr" />
      </Section>

      {/* ── PR waterfall ──────────────────────────────────────────────────── */}
      <Section title="Performance losses" color="#888">
        <PRWaterfall pr={y.pr_breakdown} />
        <div style={{ marginTop: 6 }}>
          {(Object.keys(PR_LABELS) as (keyof typeof PR_LABELS)[]).map(k => (
            <div key={k} style={rowStyle}>
              <span style={labelStyle}>{PR_LABELS[k]}</span>
              <span style={valueStyle}>{pct((y.pr_breakdown as any)[k])}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="String sizing" color="#888">
        <Row label="Modules in series" value={s.modules_in_series} />
        <Row label="Strings / MPPT"    value={s.strings_per_mppt} />
        <Row label="Tables / inverter" value={s.tables_per_inverter} />
        <Row label="String Voc"        value={s.string_voc.toFixed(0)} unit="V" />
        <Row label="String Vmp"        value={s.string_vmp.toFixed(0)} unit="V" />
      </Section>

      {/* ── Sensitivity tornado ───────────────────────────────────────────── */}
      <div style={{ ...sectionStyle, borderLeft: '3px solid #f97316' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ ...titleStyle, color: '#f97316' }}>Sensitivity</div>
          <button
            style={tornadoBtn}
            onClick={() => {
              if (!tornadoOpen) { setTornadoOpen(true); runTornado(); }
              else setTornadoOpen(false);
            }}
            disabled={!request}
            title={!request ? 'Run a layout first' : undefined}
          >
            {tornadoOpen ? 'Hide' : tornadoLoading ? '⏳ Running…' : '▶ Run'}
          </button>
        </div>

        {!request && (
          <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
            Run a layout first to enable sensitivity analysis.
          </div>
        )}

        {tornadoOpen && (
          <>
            <div style={{ fontSize: 11, color: '#888', marginTop: 4, marginBottom: 8 }}>
              Impact of ±10% change in each input on annual MWh.
            </div>
            {tornadoLoading && (
              <div style={{ fontSize: 12, color: '#888', textAlign: 'center', padding: '12px 0' }}>
                Running 6 layout variants…
              </div>
            )}
            {tornadoError && (
              <div style={{ fontSize: 12, color: '#c0392b' }}>{tornadoError}</div>
            )}
            {tornadoRows && (
              <TornadoChart rows={tornadoRows} base={y.annual_mwh} />
            )}
          </>
        )}
      </div>

      {/* ── Cables ───────────────────────────────────────────────────────── */}
      {response.cables && (
        <div style={{ ...sectionStyle, borderLeft: '3px solid #2563eb' }}>
          <div style={{ ...titleStyle, color: '#2563eb' }}>Cable Quantities</div>
          <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>
            Road-following lengths · 10% installation factor (AS/NZS 5033:2014 Cl.3.4)
          </div>

          {/* DC string — internal to tracker, not road-following */}
          <div style={rowStyle}>
            <span style={labelStyle}>DC string cable</span>
            <span style={valueStyle}>
              {(response.cables.dc_string_total_m / 1000).toFixed(1)}
              <span style={unitStyle}> km</span>
            </span>
          </div>

          {/* DC home-run — road-following tracker→inverter */}
          <div style={rowStyle}>
            <span style={labelStyle}>DC home-run cable</span>
            <span style={valueStyle}>
              {(response.cables.dc_homerun_total_m / 1000).toFixed(1)}
              <span style={unitStyle}> km</span>
            </span>
          </div>

          {/* AC MV — inverter→substation */}
          <div style={rowStyle}>
            <span style={labelStyle}>AC MV cable ({response.cables.connection_voltage_kv} kV)</span>
            <span style={valueStyle}>
              {(response.cables.ac_mv_total_m / 1000).toFixed(1)}
              <span style={unitStyle}> km</span>
            </span>
          </div>

          {/* Grand total */}
          <div style={{ ...rowStyle, borderBottom: 'none', paddingTop: 6, marginTop: 4, borderTop: '1.5px solid #dbeafe' }}>
            <span style={{ ...labelStyle, fontWeight: 700, color: '#2563eb' }}>Total cable</span>
            <span style={{ ...valueStyle, color: '#2563eb' }}>
              {((response.cables.dc_string_total_m + response.cables.dc_homerun_total_m + response.cables.ac_mv_total_m) / 1000).toFixed(1)}
              <span style={unitStyle}> km</span>
            </span>
          </div>

          <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
            <div style={cableChip('#f97316')}>
              <span style={{ width: 18, height: 3, background: '#f97316', display: 'inline-block', marginRight: 5, verticalAlign: 'middle' }} />
              DC home-run (dashed)
            </div>
            <div style={cableChip('#dc2626')}>
              <span style={{ width: 18, height: 3, background: '#dc2626', display: 'inline-block', marginRight: 5, verticalAlign: 'middle' }} />
              AC MV (solid)
            </div>
          </div>
          <div style={{ fontSize: 10, color: '#aaa', marginTop: 4 }}>
            Toggle cable overlay in map toolbar → Overlays
          </div>
        </div>
      )}

      {/* ── Financial Model ───────────────────────────────────────────────── */}
      {(response as any).financials && (() => {
        const f = (response as any).financials;
        const irr = f.project_irr_pct as number;
        const irrColor = irr >= 10 ? '#16a34a' : irr >= 7 ? '#d97706' : '#dc2626';
        return (
          <div style={{ ...sectionStyle, borderLeftColor: '#7c3aed', marginBottom: 0 }}>
            <div style={{ ...titleStyle, color: '#7c3aed' }}>Financial Model</div>

            {/* IRR highlight */}
            <div style={{
              display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' as const,
            }}>
              {[
                { label: 'Project IRR', value: `${irr.toFixed(1)}%`, color: irrColor },
                { label: 'Equity IRR', value: `${(f.equity_irr_pct as number).toFixed(1)}%`, color: '#7c3aed' },
                { label: 'LCOE', value: `$${(f.lcoe_aud_per_mwh as number).toFixed(0)}/MWh`, color: '#2E5FA3' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{
                  flex: 1, minWidth: 80, background: '#f5f3ff', borderRadius: 6,
                  padding: '6px 8px', textAlign: 'center' as const, border: '1px solid #ddd6fe',
                }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
                  <div style={{ fontSize: 9, color: '#888', marginTop: 1 }}>{label}</div>
                </div>
              ))}
            </div>

            {/* CAPEX breakdown */}
            <div style={{ ...rowStyle }}>
              <span style={labelStyle}>Total CAPEX</span>
              <span style={valueStyle}>
                ${(f.capex_total_maud as number).toFixed(1)}M
                <span style={unitStyle}> AUD  (${((f.capex_per_wp_aud as number) * 100).toFixed(0)}¢/Wp)</span>
              </span>
            </div>
            {[
              { label: 'Modules',    val: f.capex_modules_maud },
              { label: 'Trackers',   val: f.capex_trackers_maud },
              { label: 'Inverters',  val: f.capex_inverters_maud },
              { label: 'Civil',      val: f.capex_civil_maud },
              { label: 'Roads',      val: f.capex_roads_maud },
              { label: 'Cables',     val: f.capex_cables_maud },
              { label: 'Substation', val: f.capex_substation_maud },
              ...(f.capex_bess_maud > 0 ? [{ label: 'BESS', val: f.capex_bess_maud }] : []),
              { label: 'Soft costs + contingency', val: (f.capex_soft_maud + f.capex_contingency_maud + f.capex_development_maud) },
            ].map(({ label, val }) => (
              <div key={label} style={{ ...rowStyle, paddingLeft: 8 }}>
                <span style={{ ...labelStyle, fontSize: 11 }}>{label}</span>
                <span style={{ ...valueStyle, fontSize: 11, color: '#555' }}>
                  ${(val as number).toFixed(1)}M
                </span>
              </div>
            ))}

            {/* Revenue & payback */}
            <div style={{ marginTop: 8 }}>
              <div style={{ ...rowStyle }}>
                <span style={labelStyle}>Yr 1 revenue</span>
                <span style={valueStyle}>${(f.year1_revenue_maud as number).toFixed(1)}M AUD</span>
              </div>
              <div style={{ ...rowStyle }}>
                <span style={labelStyle}>Yr 1 EBITDA</span>
                <span style={valueStyle}>${(f.year1_ebitda_maud as number).toFixed(1)}M AUD</span>
              </div>
              <div style={{ ...rowStyle }}>
                <span style={labelStyle}>Simple payback</span>
                <span style={valueStyle}>{(f.simple_payback_yr as number).toFixed(1)} yrs</span>
              </div>
              <div style={{ ...rowStyle, borderBottom: 'none' }}>
                <span style={labelStyle}>NPV (8% WACC, 30yr)</span>
                <span style={{ ...valueStyle, color: f.npv_maud > 0 ? '#16a34a' : '#dc2626' }}>
                  ${(f.npv_maud as number).toFixed(1)}M AUD
                </span>
              </div>
            </div>

            <div style={{ fontSize: 9, color: '#aaa', marginTop: 6 }}>
              WA 2024 benchmarks · PPA $65/MWh · 70% gearing · 8% WACC · 30yr life
            </div>
          </div>
        );
      })()}

      <div style={{ height: 20 }} />
    </div>
  );
}

// ── KPI card ──────────────────────────────────────────────────────────────────

function KPI({ label, value, unit, color }: {
  label: string; value: string; unit: string; color: string;
}) {
  return (
    <div style={{ textAlign: 'center' as const, flex: 1 }}>
      <div style={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>{unit}</div>
      <div style={{ fontSize: 10, color: '#aaa', marginTop: 1 }}>{label}</div>
    </div>
  );
}

function cableChip(color: string): React.CSSProperties {
  return {
    fontSize: 10, color: '#555', display: 'flex', alignItems: 'center',
    background: '#f8f9fa', border: `1px solid ${color}33`,
    borderRadius: 4, padding: '2px 7px',
  };
}

// ── Styles ────────────────────────────────────────────────────────────────────

const legendText: React.CSSProperties = { fontSize: 10, color: '#555' };

const panelStyle: React.CSSProperties = {
  height: '100%', overflowY: 'auto',
  fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
  fontSize: 13, background: '#fff',
};
const kpiBar: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between',
  padding: '14px 10px 12px',
  background: 'linear-gradient(135deg, #1F3864, #17607A)',
  borderBottom: '2px solid #2E5FA3',
};
const sectionStyle: React.CSSProperties = {
  margin: '10px 12px 0', borderRadius: 7,
  background: '#f9fbfc', padding: '10px 12px',
  borderLeft: '3px solid #2E5FA3',
};
const titleStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.7px', marginBottom: 8,
};
const rowStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between',
  padding: '3px 0', borderBottom: '1px solid #e0e5ee',
};
const labelStyle: React.CSSProperties = { color: '#777', fontSize: 12 };
const valueStyle: React.CSSProperties = { fontWeight: 600, color: '#404040', fontSize: 12 };
const unitStyle: React.CSSProperties  = { fontWeight: 400, color: '#777' };
const optimiserBox: React.CSSProperties = {
  margin: '10px 12px 0', background: '#d5eddf', borderRadius: 7,
  padding: '10px 12px', border: '1px solid #a8ddb5',
};
const optimiserTitle: React.CSSProperties = {
  fontWeight: 700, fontSize: 11, textTransform: 'uppercase',
  letterSpacing: '0.7px', color: '#1E6B3C', marginBottom: 8,
};
const optimiserRow: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between',
  fontSize: 12, padding: '3px 0', borderBottom: '1px solid #c0ddc7',
  color: '#2a5a35',
};
const tornadoBtn: React.CSSProperties = {
  fontSize: 11, padding: '3px 10px',
  background: '#fff3ec', color: '#f97316',
  border: '1px solid #fed7aa', borderRadius: 5,
  cursor: 'pointer', fontWeight: 600,
};
